import type { Tier, TierName, AggregatedStats } from './types';

// ─── Tier definitions ─────────────────────────────────────────────
// Thresholds are calibrated so pot rate and avg break are coherent:
// you can only avg break 5 if you're potting ~48%+, etc.
//
//   Beginner:         0 avg break, 0% pot rate
//   Amateur:          5 avg break, 48% pot rate
//   Advanced Amateur: 12 avg break, 60% pot rate
//   Semi-Pro:         19 avg break, 67% pot rate
//   Pro Candidate:    25 avg break, 74% pot rate
//   Professional:     30 avg break, 80% pot rate

export const TIERS: Tier[] = [
  {
    name: 'Beginner',
    slug: 'beginner',
    color: '#7f8c8d',
    minAvgBreak: 0,
    minPotRate: 0,
    minSafetyRate: 0,
    description: 'Learning the game — focusing on cue ball control and basic potting.',
    proEquivalent: 'Club social player',
  },
  {
    name: 'Amateur',
    slug: 'amateur',
    color: '#2980b9',
    minAvgBreak: 5,
    minPotRate: 48,
    minSafetyRate: 40,
    description: 'Consistent potting developing. Can build small breaks and play basic safety.',
    proEquivalent: 'Local league player',
  },
  {
    name: 'Advanced Amateur',
    slug: 'advanced-amateur',
    color: '#16a085',
    minAvgBreak: 12,
    minPotRate: 60,
    minSafetyRate: 52,
    description: 'Making regular breaks above 20. Good positional play and developing safety game.',
    proEquivalent: 'Competitive club / regional level',
  },
  {
    name: 'Semi-Pro',
    slug: 'semi-pro',
    color: '#8e44ad',
    minAvgBreak: 19,
    minPotRate: 67,
    minSafetyRate: 62,
    description: 'Strong tactical game. Regularly makes breaks of 30–50 and wins on safety.',
    proEquivalent: 'County / national amateur level',
  },
  {
    name: 'Pro Candidate',
    slug: 'pro-candidate',
    color: '#d35400',
    minAvgBreak: 25,
    minPotRate: 74,
    minSafetyRate: 70,
    description: 'High-quality all-round game. Capable of centuries. Consistent under pressure.',
    proEquivalent: 'Q-School / top amateur level',
  },
  {
    name: 'Professional',
    slug: 'professional',
    color: '#c9a84c',
    minAvgBreak: 30,
    minPotRate: 80,
    minSafetyRate: 78,
    description: 'Elite consistent performance across all areas. Comparable to tour-level play.',
    proEquivalent: 'World Snooker Tour',
  },
];

/**
 * Calculate the tier for a player based on their trailing stats.
 * All three criteria must be met to qualify for a tier.
 */
export function calculateTier(
  avgBreak: number,
  potRate: number,
  safetyRate: number
): Tier {
  let result = TIERS[0];
  for (const tier of TIERS) {
    if (
      avgBreak   >= tier.minAvgBreak &&
      potRate    >= tier.minPotRate &&
      safetyRate >= tier.minSafetyRate
    ) {
      result = tier;
    }
  }
  return result;
}

/**
 * Returns 0–100 progress toward the next tier.
 */
export function progressToNextTier(
  avgBreak: number,
  potRate: number,
  safetyRate: number
): {
  nextTier: Tier | null;
  progress: number;
  gaps: { avgBreak: number; potRate: number; safetyRate: number };
} {
  const currentTier = calculateTier(avgBreak, potRate, safetyRate);
  const currentIdx  = TIERS.indexOf(currentTier);
  const nextTier    = TIERS[currentIdx + 1] ?? null;

  if (!nextTier) {
    return { nextTier: null, progress: 100, gaps: { avgBreak: 0, potRate: 0, safetyRate: 0 } };
  }

  const gaps = {
    avgBreak:   Math.max(0, nextTier.minAvgBreak   - avgBreak),
    potRate:    Math.max(0, nextTier.minPotRate     - potRate),
    safetyRate: Math.max(0, nextTier.minSafetyRate  - safetyRate),
  };

  const breakProg  = avgBreak   / nextTier.minAvgBreak;
  const potProg    = potRate    / nextTier.minPotRate;
  const safetyProg = safetyRate / nextTier.minSafetyRate;
  const progress   = Math.round(Math.min(breakProg, potProg, safetyProg) * 100);

  return { nextTier, progress, gaps };
}

export function tierFromStats(stats: Pick<AggregatedStats, 'avgBreak30d' | 'avgPotRate30d' | 'avgSafetyRate30d'>): Tier {
  return calculateTier(stats.avgBreak30d, stats.avgPotRate30d, stats.avgSafetyRate30d);
}

export function getTierByName(name: TierName): Tier {
  return TIERS.find(t => t.name === name) ?? TIERS[0];
}